/**
 * Cable profiles -- group 09.
 *
 * Triode Wire Labs, Zavfino, LessLoss, Entreq, Telos Audio Design,
 * Tchernov Cable, Melodika, ViaBlue.
 *
 * Same rule as the rest of the directory: state construction, let the kit
 * derive R, L and C from gauge, material and geometry unless the maker
 * genuinely publishes figures. Almost none of the brands in this group do --
 * they publish conductor cross-section, connector type and current rating,
 * which is exactly what the derivation needs, so no electrical numbers are
 * passed in here. Nothing in these notes claims a cable makes anything
 * "warmer", "blacker" or "more open", because there is no mechanism in this
 * engine for that and none is asserted.
 *
 * Power, USB and Ethernet entries describe the build and stop there. The
 * engine applies identity to all three types. That is not a shortcoming of the
 * cables -- several of them are excellent pieces of mechanical and electrical
 * engineering -- it is a statement about what a mains cord or a packetised
 * link can do to an audio signal.
 */

import type { CableProfile } from '../../types/hardware';
import { makeCable } from '../cableKit';
import { usd } from '../kit';

export const CABLES_09: CableProfile[] = [
  // =========================================================================
  // Triode Wire Labs -- United States
  // A small shop built almost entirely around mains cords, plus one
  // interconnect and one speaker cable. Heavy American-made copper,
  // cryogenic treatment, and bought-in Wattgate / Furutech connectors.
  // =========================================================================
  makeCable({
    mfr: 'Triode Wire Labs',
    model: 'Digital American',
    cableType: 'power',
    price: usd(450, 'APPROXIMATE', 2024, 'Approximate price for a 6 ft (1.8 m) cord; length and connector choice move it.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#b3252b',
    tags: ['cryo-treated', 'entry-line'],
    provenanceNote:
      'Model and position in the line are from the maker\'s catalogue. Conductor gauge, geometry and current rating are the class norm for a 15 A source-component cord and are estimated, not published. No electrical figures are claimed.',
    notes:
      'The lighter cord in the range, sold for digital source components rather than power amplifiers. Roughly 12 AWG conductors, cryogenically treated, with a moulded 15 A IEC. The engine applies no signal processing to mains cords, so choosing this changes the chain description and the price and leaves the audio bit-identical.',
  }),

  makeCable({
    mfr: 'Triode Wire Labs',
    model: 'Seven Plus',
    cableType: 'power',
    price: usd(800, 'APPROXIMATE', 2024, 'Approximate price for a 6 ft (1.8 m) cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 15,
    termination: ['IEC C13', 'IEC C15', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#b3252b',
    tags: ['cryo-treated', 'high-current', 'mid-line'],
    provenanceNote:
      'Model name and line position are from the maker\'s catalogue. Gauge and geometry are estimated from the cord diameter and its stated suitability for power amplifiers; the maker publishes no electrical data.',
    notes:
      'Heavy 10 AWG twisted conductor set with a flexible jacket, rated for a 15 A outlet. Gauge is the one number on a mains cord that has a defensible engineering meaning: 10 AWG keeps voltage drop under a large transient current draw low. That is a statement about the mains side of the rectifier, not about the audio, and the engine applies no DSP here.',
  }),

  makeCable({
    mfr: 'Triode Wire Labs',
    model: 'Ten Plus',
    cableType: 'power',
    price: usd(1200, 'APPROXIMATE', 2024, 'Approximate price for a 6 ft (1.8 m) cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 20,
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#b3252b',
    tags: ['cryo-treated', 'high-current', '20a-option'],
    provenanceNote:
      'Line position and connector options are from the maker\'s catalogue. Gauge and geometry are estimated; no electrical figures are published for this cord.',
    notes:
      'A step up in conductor mass and connector grade over the Seven Plus, with a 20 A C19 option for amplifiers with the larger inlet. Build quality here is real and worth the money to somebody who wants a cord that will not sag out of a heavy inlet. The engine still applies zero signal processing to it.',
  }),

  makeCable({
    mfr: 'Triode Wire Labs',
    model: 'Obsession',
    cableType: 'power',
    price: usd(1900, 'APPROXIMATE', 2024, 'Approximate price for a 6 ft (1.8 m) cord with the upgraded connector set.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 20,
    termination: ['IEC C15', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#b3252b',
    tags: ['cryo-treated', 'flagship', 'high-current'],
    provenanceNote:
      'Flagship status and connector options are from the maker\'s catalogue. Conductor purity, gauge and geometry are estimated from the maker\'s general descriptions rather than a published specification.',
    notes:
      'The top mains cord in the range: the largest conductor bundle the maker offers, cryogenically treated, terminated with high-grade rhodium or copper-alloy connectors. This is an expensive, well made cord, and the engine applies no processing to it whatsoever -- the audio path is identical to the one you get with the cord that came in the box. Both things are true at once.',
  }),

  makeCable({
    mfr: 'Triode Wire Labs',
    model: 'Spirit Interconnect',
    cableType: 'interconnect',
    price: usd(500, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Braided shield tied at the source end',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#b3252b',
    tags: ['cryo-treated', 'shielded'],
    provenanceNote:
      'Model name and available terminations are from the maker\'s catalogue; gauge, geometry and shield arrangement are estimated from the class and are not published figures.',
    notes:
      'A shielded twisted pair in the usual line-level gauge. At the derived 70 pF/m a 1 m run presents about 70 pF, which against a 600 ohm tube output stage puts the low-pass corner near 4 MHz and against a typical 100 ohm solid-state output near 23 MHz. The engine computes that and reports the in-band result, which rounds to 0.00 dB.',
  }),

  makeCable({
    mfr: 'Triode Wire Labs',
    model: 'American Speaker Cable',
    cableType: 'speaker',
    price: usd(1100, 'APPROXIMATE', 2024, 'Approximate price for a terminated 3 m pair.'),
    priceLengthM: 3,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#b3252b',
    tags: ['cryo-treated'],
    provenanceNote:
      'Model name is from the maker\'s catalogue. Gauge and geometry are estimated from the cable\'s physical description; no R, L or C figures are published.',
    notes:
      'Twisted heavy-gauge copper in a cotton-filled jacket. At 12 AWG the derived loop resistance is about 0.010 ohm/m, so a 3 m run adds roughly 0.031 ohm in series with the loudspeaker -- about 0.07 dB of level into a 4 ohm load, and a real if modest reduction in the damping factor the amplifier presents. That is the entire mechanism, and it is the same one a 12 AWG cable from anywhere else provides.',
  }),

  // =========================================================================
  // Zavfino -- Canada
  // Broad range built on OCC copper, the phono cables inherited from the
  // 1877Phono side of the business.
  // =========================================================================
  makeCable({
    mfr: 'Zavfino',
    model: 'Prima MKII Power Cable',
    cableType: 'power',
    price: usd(299, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Canada',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    shielding: 'Foil plus braid over the conductor bundle',
    termination: ['IEC C13', 'NEMA 5-15', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#1f6fb2',
    tags: ['shielded', 'entry-line'],
    provenanceNote:
      'Conductor material and line position are from the maker\'s product descriptions. Gauge and geometry are estimated from the cord diameter and rating; no electrical figures are published.',
    notes:
      'The entry cord: three twisted OCC copper conductors of roughly 12 AWG, foil and braid screened, with the maker\'s own machined connector shells. Well built at the price. Mains cords receive no signal processing in this engine, so nothing downstream of the wall changes when it is fitted.',
  }),

  makeCable({
    mfr: 'Zavfino',
    model: 'Fusion Power Cable',
    cableType: 'power',
    price: usd(650, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Canada',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 15,
    shielding: 'Foil plus braid',
    termination: ['IEC C13', 'IEC C15', 'NEMA 5-15', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#1f6fb2',
    tags: ['shielded', 'mid-line', 'high-current'],
    provenanceNote:
      'Line position and conductor material are from the maker\'s descriptions; gauge and geometry are estimated from the cord\'s size and rating.',
    notes:
      'Larger conductor bundle than the Prima, around 11 AWG, in a shielded twisted arrangement. The heavier gauge lowers voltage drop under transient current draw at the inlet of a power amplifier. No DSP is applied to power cords here, and none is implied by that sentence.',
  }),

  makeCable({
    mfr: 'Zavfino',
    model: 'Silver Dart Power Cable',
    cableType: 'power',
    price: usd(1400, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Canada',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 15,
    shielding: 'Foil plus braid',
    termination: ['IEC C13', 'IEC C15', 'NEMA 5-15', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#1f6fb2',
    tags: ['shielded', 'silver-plated', 'upper-line'],
    provenanceNote:
      'Conductor description and line position are from the maker\'s catalogue. Gauge and geometry are estimated; no electrical specification is published for this cord.',
    notes:
      'Silver-plated OCC copper conductors in a screened twisted bundle with heavy machined connectors. On a mains cord the plating changes the DC resistance by well under one percent and matters mainly for corrosion resistance at the crimp. The engine applies identity to power cords, so this entry contributes price and description only.',
  }),

  makeCable({
    mfr: 'Zavfino',
    model: 'Arcadia Power Cable',
    cableType: 'power',
    price: usd(2300, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord; dealer and length dependent.'),
    priceLengthM: 1.5,
    country: 'Canada',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 20,
    shielding: 'Multi-layer foil and braid',
    termination: ['IEC C15', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#1f6fb2',
    tags: ['shielded', 'flagship', 'high-current', '7n-occ'],
    provenanceNote:
      'Sold under the maker\'s 7N OCC designation; the flagship status and connector options are from the catalogue. Gauge and geometry are estimated from the cord diameter and rating rather than a published sheet.',
    notes:
      'The flagship mains cord, built around very high purity OCC copper of about 10 AWG with a multi-layer screen and a 20 A connector option. It is a genuinely serious piece of construction. It also receives exactly zero signal processing from this engine, which is the honest position on what a mains cord upstream of a rectifier and a bank of reservoir capacitors can do to an audio waveform.',
  }),

  makeCable({
    mfr: 'Zavfino',
    model: 'Prima MKII Interconnect',
    cableType: 'interconnect',
    price: usd(249, 'CURRENT_RETAIL', 2024, 'Price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Canada',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Foil plus braid, floated at the load end',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#1f6fb2',
    tags: ['shielded', 'entry-line'],
    provenanceNote:
      'Conductor material and terminations are from the maker\'s catalogue; gauge, geometry and shield termination are estimated from the class norm.',
    notes:
      'Screened OCC twisted pair with the maker\'s machined RCA and XLR plugs. Around 70 pF/m derived, so a 1 m pair adds roughly 70 pF of load to the source. Against any normal line output that corner sits in the megahertz, and the engine will report an in-band deviation of about 0.00 dB. That is the correct answer for a competent interconnect of this length.',
  }),

  makeCable({
    mfr: 'Zavfino',
    model: 'Silver Dart Interconnect',
    cableType: 'interconnect',
    price: usd(999, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Canada',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Foil plus braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#1f6fb2',
    tags: ['shielded', 'silver-plated', 'upper-line'],
    provenanceNote:
      'Conductor description and terminations are from the maker\'s catalogue; gauge and geometry are estimated. No capacitance figure is published.',
    notes:
      'Silver-plated OCC conductors, screened, in a twisted pair. The plating lowers loop resistance by under a percent against plain copper -- irrelevant at line level, where the source impedance is orders of magnitude larger than the cable. The number that can matter is capacitance, and at this geometry and length it is around 70 pF, which is unremarkable and harmless.',
  }),

  makeCable({
    mfr: 'Zavfino',
    model: 'Majestic MKII Phono Cable',
    cableType: 'interconnect',
    price: usd(700, 'APPROXIMATE', 2024, 'Approximate price for a 1.2 m tonearm lead with ground wire.'),
    priceLengthM: 1.2,
    country: 'Canada',
    conductor: 'occ-copper',
    geometry: 'coaxial',
    gauge: 24,
    shielding: 'Braided shield, separate ground lead',
    termination: ['RCA', 'DIN', 'XLR'],
    defaultLengthM: 1.2,
    confidence: 'ESTIMATED',
    accent: '#1f6fb2',
    tags: ['phono', 'tonearm', 'shielded'],
    provenanceNote:
      'The phono and tonearm cables come from the 1877Phono side of the business; model name and termination options are from the catalogue. Gauge and geometry are estimated from the class. Capacitance is derived here, not published, and for a phono lead the published figure would be the important one.',
    notes:
      'A tonearm lead: coaxial per channel, braided screen, separate ground wire, DIN or RCA at the arm end. This is the one interconnect application where cable capacitance genuinely matters, because it sits in parallel with the moving-magnet cartridge load and shifts the resonance formed with the cartridge inductance. At the derived 85 pF/m a 1.2 m lead adds roughly 100 pF, which lands inside the 100 to 200 pF window most MM cartridges are specified into. With a moving coil the figure is irrelevant.',
  }),

  makeCable({
    mfr: 'Zavfino',
    model: 'Prima MKII Speaker Cable',
    cableType: 'speaker',
    price: usd(549, 'CURRENT_RETAIL', 2024, 'Price for a terminated 3 m pair.'),
    priceLengthM: 3,
    country: 'Canada',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#1f6fb2',
    tags: ['entry-line'],
    provenanceNote:
      'Conductor material and terminations from the maker\'s catalogue; gauge and geometry estimated from the cable\'s physical description. No published R, L or C.',
    notes:
      'Twisted OCC copper of about 12 AWG per polarity. Derived loop resistance is roughly 0.010 ohm/m, so a 3 m run adds about 0.031 ohm -- around 0.07 dB into 4 ohms, slightly more into the impedance minimum of a demanding loudspeaker. Twisting keeps loop inductance near 0.6 uH/m, which at 3 m is about 0.23 ohm of reactance at 20 kHz.',
  }),

  makeCable({
    mfr: 'Zavfino',
    model: 'Silver Dart Speaker Cable',
    cableType: 'speaker',
    price: usd(1899, 'APPROXIMATE', 2024, 'Approximate price for a terminated 3 m pair.'),
    priceLengthM: 3,
    country: 'Canada',
    conductor: 'silver-plated-copper',
    geometry: 'braided',
    gauge: 11,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#1f6fb2',
    tags: ['silver-plated', 'upper-line', 'low-inductance'],
    provenanceNote:
      'Conductor description and terminations from the maker\'s catalogue; gauge and the braided construction are estimated from the cable\'s appearance and diameter rather than a published drawing.',
    notes:
      'Silver-plated OCC conductors woven rather than simply twisted. The weave is the electrically interesting part: interleaving opposing runs drops loop inductance to roughly 0.4 uH/m against 0.6 for a plain twisted pair, at the cost of higher capacitance, which a loudspeaker load does not care about. At 11 AWG the 3 m loop resistance works out near 0.025 ohm.',
  }),

  makeCable({
    mfr: 'Zavfino',
    model: 'Prima MKII USB',
    cableType: 'usb',
    price: usd(179, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    country: 'Canada',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid, separate power and data screens',
    termination: ['USB A to B', 'USB A to C'],
    confidence: 'ESTIMATED',
    accent: '#1f6fb2',
    tags: ['usb-2.0', 'shielded'],
    provenanceNote:
      'Termination options and conductor material from the catalogue; gauge and internal construction are the USB 2.0 class norm and are estimated. The 90 ohm figure is the requirement of the standard, not a measurement of this assembly.',
    notes:
      'A 90 ohm USB 2.0 assembly with the data pair screened separately from the bus power pair. That separation is sound practice for keeping bus-power noise off the data lines. It is also a link that either transfers the packets intact or reports an error, so the engine applies no signal processing to it.',
  }),

  makeCable({
    mfr: 'Zavfino',
    model: 'Silver Dart USB',
    cableType: 'usb',
    price: usd(499, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    country: 'Canada',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Multi-layer foil and braid',
    termination: ['USB A to B'],
    confidence: 'ESTIMATED',
    accent: '#1f6fb2',
    tags: ['usb-2.0', 'shielded', 'silver-plated'],
    provenanceNote:
      'Conductor description from the catalogue; gauge and impedance are the class and standard norms, estimated rather than published for this assembly.',
    notes:
      'Silver-plated conductors and a heavier screen than the Prima, terminated in machined shells. Construction is good. In an asynchronous USB DAC the conversion clock is generated at the DAC and the cable carries packets that are checked on arrival, so the engine applies no processing here and the audio is bit-identical to that from any compliant lead.',
  }),

  makeCable({
    mfr: 'Zavfino',
    model: 'Prima MKII Ethernet',
    cableType: 'ethernet',
    price: usd(179, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m patch lead.'),
    priceLengthM: 1.5,
    country: 'Canada',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 100,
    digitalStandard: 'Cat 7, S/FTP',
    shielding: 'S/FTP, individually foiled pairs inside an overall braid',
    termination: ['RJ45'],
    confidence: 'ESTIMATED',
    accent: '#1f6fb2',
    tags: ['cat7', 'shielded'],
    provenanceNote:
      'Category and shielding from the maker\'s description; gauge and the 100 ohm figure are the requirements of the category rather than measurements of this lead.',
    notes:
      'Four individually foiled pairs inside an overall braid, terminated in shielded RJ45 plugs. Ethernet is packetised, checksummed and retransmitted on error, and a streamer holds seconds of buffer ahead of the converter, so there is no analog path between the two ends. The engine applies no signal processing to Ethernet.',
  }),

  // =========================================================================
  // LessLoss -- Lithuania
  // Two distinct construction families: the older DFPC mains cords and the
  // C-MARC bifilar Litz weave used across the current range.
  // =========================================================================
  makeCable({
    mfr: 'LessLoss',
    model: 'DFPC Original',
    cableType: 'power',
    price: usd(650, 'APPROXIMATE', 2023, 'Approximate price for a 1.6 m cord.'),
    priceLengthM: 1.6,
    country: 'Lithuania',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    termination: ['IEC C13', 'Schuko', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#2f3b45',
    tags: ['dfpc', 'entry-line'],
    provenanceNote:
      'Model and line position from the maker\'s catalogue. Gauge and geometry are estimated from the cord\'s stated rating and diameter; the maker publishes no resistance, inductance or capacitance for it.',
    notes:
      'The first of the Dynamic Filtering Power Cable series: a thick, stiff, 16 A rated cord of roughly 12 AWG. The maker describes a proprietary conductor treatment; no mechanism for it is asserted here. What the entry records is gauge, rating and connectors, and the engine applies no signal processing to mains cords.',
  }),

  makeCable({
    mfr: 'LessLoss',
    model: 'DFPC Signature',
    cableType: 'power',
    price: usd(1100, 'APPROXIMATE', 2023, 'Approximate price for a 1.6 m cord.'),
    priceLengthM: 1.6,
    country: 'Lithuania',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 16,
    termination: ['IEC C13', 'IEC C19', 'Schuko', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#2f3b45',
    tags: ['dfpc', 'mid-line', 'high-current'],
    provenanceNote:
      'Line position from the maker\'s catalogue; gauge and geometry estimated from the cord diameter and rating. No electrical figures are published.',
    notes:
      'The middle DFPC, with a larger conductor bundle than the Original and the same 16 A rating. A heavier gauge is a real advantage on the mains side of a power amplifier, where peak current draw is large and brief. It has no path to the audio signal, and the engine models none.',
  }),

  makeCable({
    mfr: 'LessLoss',
    model: 'DFPC Reference',
    cableType: 'power',
    price: usd(1900, 'APPROXIMATE', 2023, 'Approximate price for a 1.6 m cord.'),
    priceLengthM: 1.6,
    country: 'Lithuania',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 16,
    termination: ['IEC C13', 'IEC C19', 'Schuko', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#2f3b45',
    tags: ['dfpc', 'flagship-dfpc', 'high-current'],
    provenanceNote:
      'Line position from the maker\'s catalogue; gauge and geometry estimated. The maker publishes no electrical specification for the DFPC series.',
    notes:
      'Top of the DFPC range, with the heaviest conductor set and the best connector grade of the three. An expensive, thoroughly made mains cord that this engine treats as electrically transparent, because that is what the physics supports for a cord feeding a rectifier and a reservoir bank.',
  }),

  makeCable({
    mfr: 'LessLoss',
    model: 'C-MARC Power Cable',
    cableType: 'power',
    price: usd(1300, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Lithuania',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 12,
    currentRatingA: 16,
    termination: ['IEC C13', 'IEC C19', 'Schuko', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f3b45',
    tags: ['c-marc', 'bifilar', 'counter-polarised'],
    provenanceNote:
      'The bifilar counter-polarised Litz construction is described in detail by the maker and is the basis for the geometry and conductor entries here. Gauge is the equivalent for the total conductor bundle and is estimated. No R, L or C figures are published.',
    notes:
      'C-MARC is the maker\'s bifilar Litz weave: many individually enamelled strands wound as two counter-polarised coils around a cotton core, so that the loop area of one coil opposes the other. That is a genuine, checkable construction, and its electrical consequence is low loop inductance and comparatively high capacitance -- the standard Litz trade. On a mains cord none of that reaches the audio signal, and the engine applies identity.',
  }),

  makeCable({
    mfr: 'LessLoss',
    model: 'C-MARC Entropic Process Power Cable',
    cableType: 'power',
    price: usd(2000, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Lithuania',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 12,
    currentRatingA: 16,
    termination: ['IEC C13', 'IEC C19', 'Schuko', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#2f3b45',
    tags: ['c-marc', 'bifilar', 'entropic-process'],
    provenanceNote:
      'Same C-MARC construction as the standard cord, sold as a separate SKU after an additional proprietary treatment the maker does not describe in physical terms. Gauge estimated; no electrical figures published, and none inferred from the treatment.',
    notes:
      'Physically the C-MARC cord with an additional treatment applied after manufacture. The construction facts are unchanged: bifilar counter-polarised Litz, 16 A rating, roughly 12 AWG equivalent. This database records no property for the treatment because none is published in measurable terms, and mains cords receive no processing here in any case.',
  }),

  makeCable({
    mfr: 'LessLoss',
    model: 'C-MARC Prime Power Cable',
    cableType: 'power',
    price: usd(2900, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Lithuania',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 11,
    currentRatingA: 16,
    termination: ['IEC C13', 'IEC C19', 'Schuko', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#2f3b45',
    tags: ['c-marc', 'prime', 'bifilar', 'flagship'],
    provenanceNote:
      'Prime is the maker\'s current top C-MARC variant; the family construction is published, the exact strand count and gauge for this version are not, so gauge here is estimated from the cord diameter.',
    notes:
      'The heaviest C-MARC mains cord, using a larger bundle of the same bifilar Litz. Note what is and is not claimed: the weave is real, the strand enamelling is real, the 16 A rating is real, and the engine applies exactly no DSP to it. A three thousand dollar cord that changes nothing in the signal path is a coherent object -- it is jewellery-grade engineering on the supply side.',
  }),

  makeCable({
    mfr: 'LessLoss',
    model: 'C-MARC Loudspeaker Cable',
    cableType: 'speaker',
    price: usd(3400, 'APPROXIMATE', 2024, 'Approximate price for a terminated 2 m pair.'),
    priceLengthM: 2,
    country: 'Lithuania',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 14,
    termination: ['spade', 'banana', 'bare'],
    defaultLengthM: 2,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f3b45',
    tags: ['c-marc', 'bifilar', 'counter-polarised'],
    provenanceNote:
      'Construction is from the maker\'s published description of the C-MARC weave. Effective gauge is estimated from the strand count and diameter the maker quotes for the loudspeaker version; no R, L or C figures are published.',
    notes:
      'The C-MARC weave applied to a loudspeaker run. The counter-polarised coils give this the low inductance of a Litz geometry, near 0.35 uH/m, against roughly 0.6 for an ordinary twisted pair -- worth about a tenth of a dB at 20 kHz on a short run into a low-impedance load. The cost is the resistance: the total copper cross-section here is modest, around 14 AWG equivalent, so a 2 m pair adds roughly 0.033 ohm, which is more series resistance than a plain 12 AWG cable of the same length would.',
  }),

  makeCable({
    mfr: 'LessLoss',
    model: 'C-MARC Prime Loudspeaker Cable',
    cableType: 'speaker',
    price: usd(5900, 'APPROXIMATE', 2024, 'Approximate price for a terminated 2 m pair.'),
    priceLengthM: 2,
    country: 'Lithuania',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 13,
    termination: ['spade', 'banana', 'bare'],
    defaultLengthM: 2,
    confidence: 'ESTIMATED',
    accent: '#2f3b45',
    tags: ['c-marc', 'prime', 'bifilar', 'flagship'],
    provenanceNote:
      'Prime uses the published C-MARC weave with a larger conductor bundle; the exact strand count is not published, so the effective gauge here is estimated from the stated cable diameter.',
    notes:
      'A heavier version of the same bifilar Litz weave, so the geometry-driven inductance stays near 0.35 uH/m while loop resistance falls with the larger copper section -- about 0.026 ohm over a 2 m pair at the estimated 13 AWG equivalent. Those two numbers, and the length you choose, are the whole of what this engine does with a loudspeaker cable.',
  }),

  makeCable({
    mfr: 'LessLoss',
    model: 'Anchorwave Loudspeaker Cable',
    cableType: 'speaker',
    price: usd(3500, 'APPROXIMATE', 2024, 'Approximate price for a terminated 2 m pair.'),
    priceLengthM: 2,
    country: 'Lithuania',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 13,
    termination: ['spade', 'banana', 'bare'],
    defaultLengthM: 2,
    confidence: 'ESTIMATED',
    accent: '#2f3b45',
    tags: ['anchorwave', 'low-inductance'],
    provenanceNote:
      'Anchorwave is a separate line from C-MARC in the maker\'s catalogue. Its internal construction is described only in general terms, so conductor, geometry and gauge here are estimated from the cable\'s dimensions and the maker\'s language rather than a specification.',
    notes:
      'A separate construction family from C-MARC, using an interleaved multi-conductor arrangement. Treated here as a braided geometry: loop inductance near 0.4 uH/m, capacitance around 105 pF/m, and about 0.026 ohm of loop resistance over a 2 m pair. All three are derived from gauge and geometry, not published, and the entry says so.',
  }),

  makeCable({
    mfr: 'LessLoss',
    model: 'C-MARC Interconnect',
    cableType: 'interconnect',
    price: usd(1500, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Lithuania',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 22,
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f3b45',
    tags: ['c-marc', 'bifilar', 'counter-polarised'],
    provenanceNote:
      'Construction from the maker\'s published description of the C-MARC weave; gauge estimated from the strand count quoted for the interconnect version. Capacitance is derived from the Litz geometry, not published.',
    notes:
      'The bifilar Litz weave at line level, in RCA and XLR. The derived capacitance for a Litz geometry is around 120 pF/m, higher than an ordinary twisted pair, so a 1 m pair presents roughly 120 pF to the source. Against a 600 ohm output that puts the corner near 2 MHz; against a high-impedance passive volume control of a few kilohms it comes down towards 1 MHz. Still far above the audio band, and the engine reports the in-band figure it computes rather than a story.',
  }),

  makeCable({
    mfr: 'LessLoss',
    model: 'Anchorwave Interconnect',
    cableType: 'interconnect',
    price: usd(1900, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Lithuania',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 22,
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#2f3b45',
    tags: ['anchorwave'],
    provenanceNote:
      'Anchorwave construction is described only in general terms by the maker, so conductor, geometry and gauge are estimated from the cable\'s dimensions and appearance. No electrical figures are published.',
    notes:
      'The Anchorwave construction at line level. Derived capacitance for the braided geometry is around 105 pF/m, so a 1 m pair loads the source with roughly 105 pF. Whether that is audible depends entirely on the source output impedance, which the engine already knows from the component upstream, so it computes the corner rather than asserting one.',
  }),

  // =========================================================================
  // Entreq -- Sweden
  // Best known for grounding boxes; the cable range shares the same
  // philosophy of unshielded, low-mass construction in natural dielectrics.
  // Grounding leads are not represented here because they are not one of the
  // five cable types this engine models.
  // =========================================================================
  makeCable({
    mfr: 'Entreq',
    model: 'Discover Interconnect',
    cableType: 'interconnect',
    price: usd(450, 'APPROXIMATE', 2023, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Sweden',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 22,
    shielding: 'Unshielded by design',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#6b8f3a',
    tags: ['unshielded', 'entry-line', 'natural-dielectric'],
    provenanceNote:
      'Entreq publishes almost no construction data. The line hierarchy and the unshielded approach are from the maker\'s general descriptions; conductor material, geometry and gauge are the class norm for this style of cable and are estimated.',
    notes:
      'Entry point to the range: solid copper conductors in a low-density natural dielectric, with no screen. Omitting the shield lowers capacitance -- the derived figure for a solid-core geometry is about 45 pF/m against 70 for a twisted pair -- and raises susceptibility to induced interference in an electrically noisy room. The engine models the capacitance; it does not model interference pickup, and this entry does not claim it as a benefit or a fault.',
  }),

  makeCable({
    mfr: 'Entreq',
    model: 'Konstantin Interconnect',
    cableType: 'interconnect',
    price: usd(800, 'APPROXIMATE', 2023, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Sweden',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 22,
    shielding: 'Unshielded by design',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#6b8f3a',
    tags: ['unshielded', 'natural-dielectric'],
    provenanceNote:
      'Line position is from the maker\'s catalogue; conductor, geometry and gauge are estimated because Entreq publishes no specification for its cables.',
    notes:
      'A step up in the range, same unshielded solid-core approach. Around 45 pF/m derived, so a 1 m pair adds roughly 45 pF of load -- about the lowest capacitance an interconnect construction reaches, and the reason the low-pass corner for this cable sits higher than for a screened design of the same length.',
  }),

  makeCable({
    mfr: 'Entreq',
    model: 'Atlantis Interconnect',
    cableType: 'interconnect',
    price: usd(1600, 'APPROXIMATE', 2023, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Sweden',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 21,
    shielding: 'Unshielded by design',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#6b8f3a',
    tags: ['unshielded', 'upper-line', 'natural-dielectric'],
    provenanceNote:
      'Position in the hierarchy is from the maker\'s catalogue. Conductor material, geometry and gauge are estimated; the maker publishes no electrical or dimensional data.',
    notes:
      'Upper-middle of the range, a heavier conductor set in the same unshielded dressing. The extra copper lowers series resistance, which at line level is irrelevant because the load impedance is tens of kilohms. The property that could matter is still capacitance, still around 45 pF/m derived, still negligible against any normal source.',
  }),

  makeCable({
    mfr: 'Entreq',
    model: 'Challenger Interconnect',
    cableType: 'interconnect',
    price: usd(2800, 'APPROXIMATE', 2023, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Sweden',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 21,
    shielding: 'Unshielded by design',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#6b8f3a',
    tags: ['unshielded', 'upper-line', 'natural-dielectric'],
    provenanceNote:
      'Line position from the maker\'s catalogue; every construction field here is estimated from the class, as Entreq publishes no specification.',
    notes:
      'Near the top of the interconnect range, with the largest conductor and dielectric assembly before the flagship. The construction is careful and the materials are unusual. The three numbers this engine reads are still derived from gauge, material and geometry, and they are close enough to the cheaper cables in the same line that the computed in-band difference between them is nil.',
  }),

  makeCable({
    mfr: 'Entreq',
    model: 'Apollo Interconnect',
    cableType: 'interconnect',
    price: usd(4500, 'APPROXIMATE', 2023, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Sweden',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 20,
    shielding: 'Unshielded by design',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#6b8f3a',
    tags: ['unshielded', 'flagship', 'natural-dielectric'],
    provenanceNote:
      'Flagship position from the maker\'s catalogue. Conductor, geometry and gauge are estimated; no construction or electrical specification is published.',
    notes:
      'The flagship interconnect. Reported here exactly as the rest: an unshielded solid-core assembly of roughly 20 AWG in a natural dielectric, with derived capacitance near 45 pF/m. The engine will compute the same essentially-zero in-band effect it computes for the entry model, and that is the honest output rather than a disappointing one.',
  }),

  makeCable({
    mfr: 'Entreq',
    model: 'Konstantin Speaker Cable',
    cableType: 'speaker',
    price: usd(1600, 'APPROXIMATE', 2023, 'Approximate price for a terminated 3 m pair.'),
    priceLengthM: 3,
    country: 'Sweden',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#6b8f3a',
    tags: ['unshielded', 'natural-dielectric'],
    provenanceNote:
      'Line position from the maker\'s catalogue; conductor, geometry and gauge estimated from the cable\'s external dimensions, as no specification is published.',
    notes:
      'A relatively light loudspeaker cable by the standards of its price. At the estimated 14 AWG the derived loop resistance is about 0.0166 ohm/m, so a 3 m run adds roughly 0.050 ohm in series with the driver -- around 0.11 dB into 4 ohms and a measurable reduction in damping factor. On a long run to a low-impedance loudspeaker a heavier gauge would do better, and that is a physics statement, not a criticism of the build.',
  }),

  makeCable({
    mfr: 'Entreq',
    model: 'Atlantis Speaker Cable',
    cableType: 'speaker',
    price: usd(3200, 'APPROXIMATE', 2023, 'Approximate price for a terminated 3 m pair.'),
    priceLengthM: 3,
    country: 'Sweden',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#6b8f3a',
    tags: ['unshielded', 'upper-line', 'natural-dielectric'],
    provenanceNote:
      'Line position from the maker\'s catalogue; all construction fields estimated. Entreq publishes no cable specification.',
    notes:
      'More conductor mass than the Konstantin, estimated at around 13 AWG, which brings the 3 m loop resistance down to roughly 0.040 ohm. Twisting keeps loop inductance near 0.6 uH/m, about 0.23 ohm of reactance at 20 kHz over that length. Those are the two figures the engine puts in series with the loudspeaker.',
  }),

  makeCable({
    mfr: 'Entreq',
    model: 'Challenger Speaker Cable',
    cableType: 'speaker',
    price: usd(5500, 'APPROXIMATE', 2023, 'Approximate price for a terminated 3 m pair.'),
    priceLengthM: 3,
    country: 'Sweden',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#6b8f3a',
    tags: ['unshielded', 'flagship', 'natural-dielectric'],
    provenanceNote:
      'Line position from the maker\'s catalogue; conductor, geometry and gauge estimated from the cable\'s dimensions rather than published data.',
    notes:
      'The heaviest loudspeaker cable in the range covered here, estimated at about 12 AWG per polarity, giving roughly 0.031 ohm over a 3 m pair. Compared with the Konstantin in the same line that is around 0.02 ohm less series resistance, worth some four hundredths of a decibel into 4 ohms. The number is small and it is real, and the engine will show it rather than dress it up.',
  }),

  makeCable({
    mfr: 'Entreq',
    model: 'Konstantin Power Cable',
    cableType: 'power',
    price: usd(1000, 'APPROXIMATE', 2023, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Sweden',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 10,
    termination: ['IEC C13', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#6b8f3a',
    tags: ['unshielded', 'natural-dielectric'],
    provenanceNote:
      'Line position from the maker\'s catalogue; gauge, geometry and rating are estimated from the cord diameter and the European market it is built for. Nothing electrical is published.',
    notes:
      'A mains cord in the same unshielded, natural-dielectric idiom as the interconnects, around 13 AWG with a Schuko or IEC pairing. Note the rating: a 10 A European cord is a different object from a 20 A American one, and that difference is real. It is also entirely upstream of the audio, and the engine applies no processing to power cords.',
  }),

  makeCable({
    mfr: 'Entreq',
    model: 'Atlantis Power Cable',
    cableType: 'power',
    price: usd(1800, 'APPROXIMATE', 2023, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Sweden',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    termination: ['IEC C13', 'IEC C19', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#6b8f3a',
    tags: ['unshielded', 'upper-line', 'high-current'],
    provenanceNote:
      'Line position from the maker\'s catalogue; gauge, geometry and current rating are estimated. No specification is published.',
    notes:
      'The larger mains cord, estimated at 12 AWG and a 16 A rating, with a C19 option for amplifiers using the high-current inlet. Every field in this entry is construction. There is no notes text here describing a sound, because a mains cord in this engine has no signal path and inventing one would be the fabrication this database exists to avoid.',
  }),

  // =========================================================================
  // Telos Audio Design -- Taiwan
  // The weakest recall in this group: the Gold Reference and Platinum
  // Reference series names are solid, but Telos publishes very little
  // construction detail and everything below is marked estimated.
  // =========================================================================
  makeCable({
    mfr: 'Telos Audio Design',
    model: 'Gold Reference Power Cable',
    cableType: 'power',
    price: usd(2500, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord; dealer pricing varies widely.'),
    priceLengthM: 1.5,
    country: 'Taiwan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 16,
    shielding: 'Shielded',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#c8a34a',
    tags: ['shielded', 'high-current'],
    provenanceNote:
      'The Gold Reference series name is from the maker\'s catalogue. Conductor material, gauge, geometry, rating and shielding are all estimated from the class; Telos publishes no construction or electrical specification that could be quoted here.',
    notes:
      'A heavy shielded mains cord with machined, plated connectors, the middle of the maker\'s reference tiers. The maker also describes a proprietary post-manufacture treatment; this database records no property for it because none is stated in measurable terms. Mains cords receive no signal processing in this engine.',
  }),

  makeCable({
    mfr: 'Telos Audio Design',
    model: 'Platinum Reference Power Cable',
    cableType: 'power',
    price: usd(5500, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord; dealer pricing varies widely.'),
    priceLengthM: 1.5,
    country: 'Taiwan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 20,
    shielding: 'Multi-layer shield',
    termination: ['IEC C15', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#c8a34a',
    tags: ['shielded', 'flagship', 'high-current'],
    provenanceNote:
      'Series name and flagship position are from the maker\'s catalogue; all construction fields are estimated from the class. No published specification is available to quote.',
    notes:
      'The senior reference mains cord, larger in every dimension than the Gold and fitted with the heaviest connector set. Priced like a component. Contributes nothing to the audio path in this engine, by design and by physics -- the entry exists so a chain can be described accurately and costed accurately.',
  }),

  makeCable({
    mfr: 'Telos Audio Design',
    model: 'Gold Reference Interconnect',
    cableType: 'interconnect',
    price: usd(2200, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Taiwan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Shielded',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#c8a34a',
    tags: ['shielded'],
    provenanceNote:
      'Series name from the maker\'s catalogue; conductor, geometry, gauge and shielding are estimated from the class because no specification is published.',
    notes:
      'A shielded line-level twisted pair with heavy machined plugs. The derived capacitance for the geometry is 70 pF/m, so a 1 m pair loads the source with roughly 70 pF. The engine computes the corner that forms with the source output impedance and reports the in-band deviation, which for any normal preamplifier output is far below the resolution of the display.',
  }),

  makeCable({
    mfr: 'Telos Audio Design',
    model: 'Platinum Reference Interconnect',
    cableType: 'interconnect',
    price: usd(4500, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Taiwan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Multi-layer shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#c8a34a',
    tags: ['shielded', 'flagship'],
    provenanceNote:
      'Series name from the maker\'s catalogue; every construction field is estimated. No published data exists to quote for this cable.',
    notes:
      'The senior interconnect, with a larger conductor bundle and a heavier screen than the Gold. Capacitance is still derived at 70 pF/m for the geometry, and series resistance at line level is irrelevant against a load of tens of kilohms. What this cable is, in the engine, is a 70 pF load and a very expensive line in the bill of materials.',
  }),

  makeCable({
    mfr: 'Telos Audio Design',
    model: 'Gold Reference Speaker Cable',
    cableType: 'speaker',
    price: usd(4500, 'APPROXIMATE', 2024, 'Approximate price for a terminated 2.5 m pair.'),
    priceLengthM: 2.5,
    country: 'Taiwan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    termination: ['spade', 'banana'],
    defaultLengthM: 2.5,
    confidence: 'ESTIMATED',
    accent: '#c8a34a',
    tags: ['heavy-gauge'],
    provenanceNote:
      'Series name from the maker\'s catalogue; conductor, geometry and gauge are estimated from the cable\'s dimensions. No R, L or C is published.',
    notes:
      'Heavy twisted OCC copper, estimated at 12 AWG per polarity. Over a 2.5 m pair that is roughly 0.026 ohm of loop resistance, or about 0.06 dB into a 4 ohm load, plus around 1.5 uH of loop inductance which contributes about 0.19 ohm of reactance at 20 kHz. Both figures are derived from gauge and geometry and both are shown in the inspector.',
  }),

  makeCable({
    mfr: 'Telos Audio Design',
    model: 'Platinum Reference Speaker Cable',
    cableType: 'speaker',
    price: usd(9000, 'APPROXIMATE', 2024, 'Approximate price for a terminated 2.5 m pair.'),
    priceLengthM: 2.5,
    country: 'Taiwan',
    conductor: 'occ-copper',
    geometry: 'braided',
    gauge: 11,
    termination: ['spade', 'banana'],
    defaultLengthM: 2.5,
    confidence: 'ESTIMATED',
    accent: '#c8a34a',
    tags: ['flagship', 'low-inductance'],
    provenanceNote:
      'Series name from the maker\'s catalogue. The braided construction, conductor material and gauge are estimated from the cable\'s appearance and diameter; nothing electrical is published.',
    notes:
      'The flagship loudspeaker cable, treated here as a braided geometry: loop inductance around 0.4 uH/m rather than the 0.6 of a plain twisted pair, capacitance correspondingly higher at about 105 pF/m, which a loudspeaker load ignores. At the estimated 11 AWG a 2.5 m pair contributes roughly 0.021 ohm of series resistance.',
  }),

  makeCable({
    mfr: 'Telos Audio Design',
    model: 'Gold Reference USB Cable',
    cableType: 'usb',
    price: usd(1200, 'APPROXIMATE', 2024, 'Approximate price for a 1 m A-to-B assembly.'),
    priceLengthM: 1,
    country: 'Taiwan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid',
    termination: ['USB A to B'],
    defaultLengthM: 1,
    confidence: 'ESTIMATED',
    accent: '#c8a34a',
    tags: ['usb-2.0', 'shielded'],
    provenanceNote:
      'Series name from the maker\'s catalogue; gauge, shielding and construction are the USB 2.0 class norm and are estimated. The 90 ohm figure is the requirement of the standard rather than a measurement of this assembly.',
    notes:
      'A shielded USB 2.0 assembly with machined connector shells. Everything worth saying about it is mechanical: strain relief, contact plating, screen coverage, the 90 ohm differential pair the standard requires. The transfer is packetised and error-checked, so the engine applies no signal processing and the data either arrives or does not.',
  }),

  // =========================================================================
  // Tchernov Cable -- Russia
  // A value-engineering house that publishes proper datasheets including
  // resistance, inductance and capacitance per metre. The specific figures
  // for these models are not recalled with confidence, so none are passed in
  // and the kit derives from gauge and geometry instead -- see the
  // provenance notes.
  // =========================================================================
  makeCable({
    mfr: 'Tchernov Cable',
    model: 'Standard 2 SC',
    cableType: 'speaker',
    price: usd(120, 'APPROXIMATE', 2024, 'Approximate cost of a terminated 3 m pair made from bulk cable.'),
    priceLengthM: 3,
    country: 'Russia',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 14,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#8a1f2d',
    tags: ['bulk-cable', 'entry-line'],
    provenanceNote:
      'Tchernov publishes full datasheets with resistance, inductance and capacitance per metre for its cables. The figures for this specific model are not recalled reliably, so none are asserted: R, L and C here are derived from the estimated gauge and geometry. If the datasheet is to hand, its numbers should replace them.',
    notes:
      'The entry bulk loudspeaker cable: two jacketed conductors of roughly 14 AWG lying parallel. Derived loop resistance is about 0.017 ohm/m, so a 3 m run adds around 0.050 ohm -- about 0.11 dB into 4 ohms. The close parallel spacing gives low inductance and high capacitance, which is the right trade for a loudspeaker feed.',
  }),

  makeCable({
    mfr: 'Tchernov Cable',
    model: 'Classic MK II SC',
    cableType: 'speaker',
    price: usd(260, 'APPROXIMATE', 2024, 'Approximate cost of a terminated 3 m pair.'),
    priceLengthM: 3,
    country: 'Russia',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 13,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#8a1f2d',
    tags: ['bulk-cable'],
    provenanceNote:
      'The maker publishes per-metre R, L and C on its datasheets; the values for this model are not recalled reliably and are therefore derived here from the estimated gauge and geometry rather than quoted.',
    notes:
      'A step up in cross-section over the Standard, at roughly 13 AWG per conductor, in the same two-conductor jacketed form. That works out near 0.040 ohm of loop resistance over 3 m against 0.050 for the Standard -- a difference of about 0.02 dB into 4 ohms. Small, computable, and stated rather than embellished.',
  }),

  makeCable({
    mfr: 'Tchernov Cable',
    model: 'Special XS SC',
    cableType: 'speaker',
    price: usd(520, 'APPROXIMATE', 2024, 'Approximate cost of a terminated 3 m pair.'),
    priceLengthM: 3,
    country: 'Russia',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#8a1f2d',
    tags: ['mid-line'],
    provenanceNote:
      'Gauge and the twisted construction are estimated from the cable\'s external form. The maker does publish per-metre electrical figures for this range; they are not quoted here because they are not recalled reliably, so the kit derives instead.',
    notes:
      'Roughly 12 AWG per polarity in a twisted arrangement rather than the flat pair used lower in the range. Loop resistance over 3 m comes to about 0.031 ohm. The twist raises inductance relative to a close parallel pair, to around 0.6 uH/m, which over 3 m is about 0.23 ohm of reactance at 20 kHz -- a tenth of a dB or so into a low-impedance load at the top of the band.',
  }),

  makeCable({
    mfr: 'Tchernov Cable',
    model: 'Reference SC',
    cableType: 'speaker',
    price: usd(1200, 'APPROXIMATE', 2024, 'Approximate cost of a terminated 3 m pair.'),
    priceLengthM: 3,
    country: 'Russia',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#8a1f2d',
    tags: ['upper-line', 'heavy-gauge'],
    provenanceNote:
      'Gauge and geometry estimated from the cable\'s dimensions. The maker publishes per-metre R, L and C for its ranges; those figures are not quoted here because they are not recalled reliably.',
    notes:
      'Heavier again, around 11 AWG, giving roughly 0.025 ohm of loop resistance over a 3 m pair. Beyond about this gauge the series resistance is already an order of magnitude below the loudspeaker impedance minimum, and further copper buys progressively less. The engine will show exactly how much less.',
  }),

  makeCable({
    mfr: 'Tchernov Cable',
    model: 'Ultimate SC',
    cableType: 'speaker',
    price: usd(2600, 'APPROXIMATE', 2024, 'Approximate cost of a terminated 3 m pair.'),
    priceLengthM: 3,
    country: 'Russia',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#8a1f2d',
    tags: ['flagship', 'heavy-gauge'],
    provenanceNote:
      'Flagship position from the maker\'s catalogue; conductor purity, gauge and geometry are estimated from the cable\'s dimensions. Published per-metre figures exist on the maker\'s datasheets but are not quoted here.',
    notes:
      'The top loudspeaker cable in the range, estimated at 10 AWG per polarity. A 3 m pair contributes about 0.020 ohm, roughly 0.04 dB into 4 ohms and around half the series resistance of the entry model in the same line. That is the honest size of the difference between the cheapest and the dearest cable this maker sells, as this engine computes it.',
  }),

  makeCable({
    mfr: 'Tchernov Cable',
    model: 'Standard IC',
    cableType: 'interconnect',
    price: usd(100, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Russia',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Braided shield',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#8a1f2d',
    tags: ['shielded', 'entry-line'],
    provenanceNote:
      'Construction is the class norm for this maker\'s entry interconnect and is estimated. The maker publishes per-metre capacitance on its datasheets; the figure for this model is not recalled reliably and so is derived here rather than quoted.',
    notes:
      'A screened twisted pair with moulded RCA plugs, sold at a price that reflects what the materials cost. Derived capacitance 70 pF/m, so 70 pF on a 1 m pair. The engine computes the low-pass that forms with the source impedance and reports it, which for this cable and any normal source is 0.00 dB in band.',
  }),

  makeCable({
    mfr: 'Tchernov Cable',
    model: 'Classic IC',
    cableType: 'interconnect',
    price: usd(200, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Russia',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Foil plus braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#8a1f2d',
    tags: ['shielded'],
    provenanceNote:
      'Gauge, geometry and shielding estimated from the class. Published capacitance exists on the maker\'s datasheet for this range but is not recalled reliably, so the derived figure is used.',
    notes:
      'A better-screened version of the same idea, available balanced. In the balanced form the twisted pair carries the signal differentially and the shield carries no signal current, which is the arrangement that actually rejects induced interference -- a construction fact with a mechanism, unlike most of what is written about interconnects.',
  }),

  makeCable({
    mfr: 'Tchernov Cable',
    model: 'Special XS IC',
    cableType: 'interconnect',
    price: usd(400, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Russia',
    conductor: 'ofc',
    geometry: 'star-quad',
    gauge: 22,
    shielding: 'Foil plus braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#8a1f2d',
    tags: ['shielded', 'mid-line'],
    provenanceNote:
      'The four-conductor arrangement is inferred from the cable\'s description; gauge and shielding are estimated. Per-metre capacitance is published by the maker for this range but is derived here rather than quoted.',
    notes:
      'Four conductors paralleled in opposing pairs, the standard star-quad trick: the two halves of each polarity sit diagonally opposite, so the loop area seen by an external magnetic field very nearly cancels. Derived inductance is 0.45 uH/m and capacitance 95 pF/m -- a little more capacitance than a plain pair, in exchange for real magnetic-field rejection.',
  }),

  makeCable({
    mfr: 'Tchernov Cable',
    model: 'Reference IC',
    cableType: 'interconnect',
    price: usd(900, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Russia',
    conductor: 'ofc',
    geometry: 'star-quad',
    gauge: 21,
    shielding: 'Foil plus braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#8a1f2d',
    tags: ['shielded', 'upper-line'],
    provenanceNote:
      'Construction inferred from the maker\'s description of the range; gauge estimated. Published per-metre figures are not quoted because they are not recalled reliably.',
    notes:
      'The senior interconnect in this selection, a heavier star-quad with a full foil-and-braid screen. At line level the conductor gauge makes no measurable difference; the star-quad geometry and the screen do, in interference terms, and the derived 95 pF/m is what the engine actually uses.',
  }),

  makeCable({
    mfr: 'Tchernov Cable',
    model: 'Standard AC Power',
    cableType: 'power',
    price: usd(120, 'APPROXIMATE', 2024, 'Approximate cost of a 1.5 m assembled cord.'),
    priceLengthM: 1.5,
    country: 'Russia',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    currentRatingA: 10,
    termination: ['IEC C13', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#8a1f2d',
    tags: ['entry-line'],
    provenanceNote:
      'Gauge, geometry and rating are the class norm for a 10 A European cord and are estimated from the cable\'s stated cross-section range.',
    notes:
      'A plain, honestly priced 10 A mains cord of roughly 14 AWG. It meets the safety standard, it is flexible, and it costs about what the copper and the moulding cost. The engine applies no signal processing to power cords, so this and the flagship cords elsewhere in this file produce identical audio.',
  }),

  makeCable({
    mfr: 'Tchernov Cable',
    model: 'Classic AC Power',
    cableType: 'power',
    price: usd(300, 'APPROXIMATE', 2024, 'Approximate cost of a 1.5 m assembled cord.'),
    priceLengthM: 1.5,
    country: 'Russia',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 16,
    termination: ['IEC C13', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#8a1f2d',
    tags: ['high-current'],
    provenanceNote:
      'Gauge and rating estimated from the cross-section range the maker quotes for this line; geometry inferred from the cord\'s round jacketed form.',
    notes:
      'A 16 A cord at around 13 AWG, which is a sensible pairing: the conductor is sized for the rating rather than oversized for the marketing. No DSP is applied to mains cords here.',
  }),

  makeCable({
    mfr: 'Tchernov Cable',
    model: 'Special AC Power',
    cableType: 'power',
    price: usd(600, 'APPROXIMATE', 2024, 'Approximate cost of a 1.5 m assembled cord.'),
    priceLengthM: 1.5,
    country: 'Russia',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    shielding: 'Braided screen over the conductor bundle',
    termination: ['IEC C13', 'IEC C19', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#8a1f2d',
    tags: ['shielded', 'high-current'],
    provenanceNote:
      'Gauge, screen and rating estimated from the maker\'s description of the line; no electrical specification is quoted.',
    notes:
      'The screened mains cord in the range, about 12 AWG and rated 16 A, with a C19 option. A braided screen on a mains cord is a real thing that reduces radiated emission from the cord itself into nearby signal leads. That is an interference argument, not a signal-path one, and the engine models neither for power cords.',
  }),

  makeCable({
    mfr: 'Tchernov Cable',
    model: 'Special USB A-B IC',
    cableType: 'usb',
    price: usd(220, 'APPROXIMATE', 2024, 'Approximate price for a 1 m A-to-B assembly.'),
    priceLengthM: 1,
    country: 'Russia',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid',
    termination: ['USB A to B'],
    defaultLengthM: 1,
    confidence: 'ESTIMATED',
    accent: '#8a1f2d',
    tags: ['usb-2.0', 'shielded'],
    provenanceNote:
      'Gauge and construction are the USB 2.0 class norm and are estimated; the 90 ohm figure is the standard\'s requirement, not a measurement of this assembly.',
    notes:
      'A compliant USB 2.0 A-to-B lead with a foil-and-braid screen. The differential pair is held at the 90 ohm the standard specifies so that reflections stay inside the eye. Once the packets arrive and pass their checks the bits are the bits, and this engine applies no processing to USB.',
  }),

  // =========================================================================
  // Melodika -- Poland
  // Volume manufacture for the European market: honest copper, sensible
  // cross-sections, prices measured in tens rather than thousands.
  // =========================================================================
  makeCable({
    mfr: 'Melodika',
    model: 'Brown Sugar Speaker Cable',
    cableType: 'speaker',
    price: usd(70, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m pair cut from bulk and terminated.'),
    priceLengthM: 3,
    country: 'Poland',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 13,
    termination: ['banana', 'spade', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7d4b8c',
    tags: ['bulk-cable', 'entry-line'],
    provenanceNote:
      'Sold by the metre in several cross-sections; the gauge here corresponds to the mid variant of the range. Construction from the maker\'s catalogue description. No R, L or C is published, so those are derived.',
    notes:
      'A twin-conductor jacketed cable sold from the reel, the sort of thing that gets cut to length in a shop. At roughly 13 AWG the derived loop resistance is about 0.013 ohm/m, so a 3 m run adds around 0.040 ohm -- some 0.09 dB into 4 ohms. That is within a couple of hundredths of a dB of what several cables costing thirty times as much in this same file will do.',
  }),

  makeCable({
    mfr: 'Melodika',
    model: 'Black Edition Speaker Cable',
    cableType: 'speaker',
    price: usd(150, 'APPROXIMATE', 2024, 'Approximate cost of a terminated 3 m pair.'),
    priceLengthM: 3,
    country: 'Poland',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 12,
    termination: ['banana', 'spade', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#7d4b8c',
    tags: ['bulk-cable'],
    provenanceNote:
      'Line position from the maker\'s catalogue; gauge estimated from the cross-section range offered. Geometry inferred from the cable\'s twin jacketed form.',
    notes:
      'The middle loudspeaker cable, around 12 AWG per conductor, giving roughly 0.031 ohm over a 3 m pair. The two conductors run close and parallel, which keeps loop inductance low and capacitance comparatively high -- exactly the trade you want on a loudspeaker feed, where the load is a few ohms and shunt capacitance is harmless.',
  }),

  makeCable({
    mfr: 'Melodika',
    model: 'Purple Rain Speaker Cable',
    cableType: 'speaker',
    price: usd(300, 'APPROXIMATE', 2024, 'Approximate cost of a terminated 3 m pair.'),
    priceLengthM: 3,
    country: 'Poland',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 11,
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#7d4b8c',
    tags: ['upper-line', 'heavy-gauge'],
    provenanceNote:
      'Line position from the maker\'s catalogue; gauge estimated from the largest cross-section offered in the range. No electrical figures are published.',
    notes:
      'The heaviest loudspeaker cable in the range, estimated at 11 AWG per conductor: about 0.025 ohm of loop resistance over a 3 m pair, or roughly 0.05 dB into 4 ohms. On a long run to a loudspeaker with a low impedance minimum this gauge is the thing that matters, and it costs three hundred dollars rather than three thousand.',
  }),

  makeCable({
    mfr: 'Melodika',
    model: 'Brown Sugar Interconnect',
    cableType: 'interconnect',
    price: usd(45, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Poland',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    shielding: 'Braided shield',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#7d4b8c',
    tags: ['shielded', 'entry-line'],
    provenanceNote:
      'Construction is the class norm for this maker\'s entry interconnect and is estimated; no specification is published.',
    notes:
      'A screened twisted pair with moulded RCA plugs at a supermarket price. 70 pF/m derived, so 70 pF on a 1 m pair, which is the same load the several-thousand-dollar interconnects in this file present. The engine computes the same essentially-zero in-band result for both, because that is what the arithmetic gives.',
  }),

  makeCable({
    mfr: 'Melodika',
    model: 'Black Edition Interconnect',
    cableType: 'interconnect',
    price: usd(90, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Poland',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 23,
    shielding: 'Foil plus braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#7d4b8c',
    tags: ['shielded'],
    provenanceNote:
      'Line position from the maker\'s catalogue; gauge, geometry and shielding are estimated from the class.',
    notes:
      'Better screening and machined plugs over the Brown Sugar, with a balanced version. The construction improvements are mechanical and interference-related. Derived capacitance is unchanged at 70 pF/m for the geometry, so the modelled electrical behaviour is the same.',
  }),

  makeCable({
    mfr: 'Melodika',
    model: 'Purple Rain Interconnect',
    cableType: 'interconnect',
    price: usd(180, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Poland',
    conductor: 'ofc',
    geometry: 'star-quad',
    gauge: 23,
    shielding: 'Foil plus braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#7d4b8c',
    tags: ['shielded', 'upper-line'],
    provenanceNote:
      'The four-conductor arrangement is inferred from the maker\'s description of the line; gauge and shielding are estimated. Nothing electrical is published.',
    notes:
      'The senior interconnect, treated here as a star-quad: conductors paralleled in opposing diagonal pairs so that induced magnetic pickup largely cancels. Derived figures are 0.45 uH/m and 95 pF/m -- slightly more shunt capacitance than a plain pair, which at 1 m and any normal source impedance changes nothing in band.',
  }),

  makeCable({
    mfr: 'Melodika',
    model: 'Black Edition Power Cable',
    cableType: 'power',
    price: usd(120, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Poland',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 16,
    termination: ['IEC C13', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#7d4b8c',
    tags: ['high-current'],
    provenanceNote:
      'Gauge, geometry and rating are estimated from the cross-section this maker quotes for its European mains cords.',
    notes:
      'A 16 A Schuko-to-IEC cord of roughly 13 AWG with moulded connectors. It is properly rated for the job and costs about what a well made cord should. Power cords receive no signal processing in this engine.',
  }),

  makeCable({
    mfr: 'Melodika',
    model: 'Purple Rain Power Cable',
    cableType: 'power',
    price: usd(250, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Poland',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    shielding: 'Foil screen',
    termination: ['IEC C13', 'IEC C19', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#7d4b8c',
    tags: ['shielded', 'high-current', 'upper-line'],
    provenanceNote:
      'Gauge, screen and rating estimated from the maker\'s description of the line; no electrical specification is published.',
    notes:
      'The larger mains cord, about 12 AWG and screened, with a C19 option for amplifiers using the 20 A inlet. Everything recorded here is construction: cross-section, rating, connector type, screen. There is no sonic description because a mains cord has no signal path in this engine and none is invented for it.',
  }),

  // =========================================================================
  // ViaBlue -- Germany
  // The most systematically specified brand in this group: the model numbers
  // encode conductor cross-section, so gauge here follows directly from the
  // name rather than from a guess.
  // =========================================================================
  makeCable({
    mfr: 'ViaBlue',
    model: 'SC-2 Silver-Series Speaker Cable',
    cableType: 'speaker',
    price: usd(300, 'APPROXIMATE', 2024, 'Approximate cost of a terminated 3 m pair.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 14,
    dielectric: 'Polyethylene',
    termination: ['banana', 'spade', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#0d6ea8',
    tags: ['silver-plated', 'entry-line'],
    provenanceNote:
      'ViaBlue quotes conductor cross-section in square millimetres for every cable; the gauge here is the AWG equivalent of the 2 mm2 class conductor used in this model. Inductance and capacitance are derived from the geometry, as ViaBlue does not publish them.',
    notes:
      'Two silver-plated copper conductors of about 2 mm2 each, roughly 14 AWG, twisted inside a jacket. Derived loop resistance 0.0165 ohm/m, so a 3 m run adds around 0.050 ohm. The silver plating lowers DC resistance by well under one percent against plain copper -- its real function is a stable, corrosion-resistant surface for the crimp and the plug.',
  }),

  makeCable({
    mfr: 'ViaBlue',
    model: 'SC-4 Silver-Series Speaker Cable',
    cableType: 'speaker',
    price: usd(600, 'APPROXIMATE', 2024, 'Approximate cost of a terminated 3 m pair.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'silver-plated-copper',
    geometry: 'star-quad',
    gauge: 11,
    dielectric: 'Polyethylene',
    termination: ['banana', 'spade', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#0d6ea8',
    tags: ['silver-plated', 'bi-wire-capable', 'mid-line'],
    provenanceNote:
      'Four conductors, which the maker offers either paralleled two per polarity or split for bi-wiring. Gauge here is the effective AWG when paralleled. L and C are derived from the four-conductor geometry, not published.',
    notes:
      'Four silver-plated conductors that can be paralleled two per polarity, giving an effective cross-section near 4 mm2 or about 11 AWG, or split for bi-wiring. Paralleled, a 3 m run adds roughly 0.025 ohm. The four-conductor arrangement in opposing pairs also lowers loop inductance to about 0.45 uH/m against 0.6 for a simple twisted pair.',
  }),

  makeCable({
    mfr: 'ViaBlue',
    model: 'SC-6 Silver-Series Speaker Cable',
    cableType: 'speaker',
    price: usd(900, 'APPROXIMATE', 2024, 'Approximate cost of a terminated 3 m pair.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'silver-plated-copper',
    geometry: 'braided',
    gauge: 10,
    dielectric: 'Polyethylene',
    termination: ['banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#0d6ea8',
    tags: ['silver-plated', 'flagship', 'heavy-gauge'],
    provenanceNote:
      'The largest cable in the Silver-Series speaker range; gauge is the AWG equivalent of the paralleled conductor set. The interleaved construction is described by the maker; L and C are derived from it.',
    notes:
      'The heaviest cable in the series, with the conductors interleaved rather than simply laid together. Effective gauge around 10 AWG, so a 3 m run contributes about 0.020 ohm -- roughly 0.04 dB into a 4 ohm load, and about 0.06 dB into a 3 ohm impedance minimum. Interleaving keeps loop inductance near 0.4 uH/m.',
  }),

  makeCable({
    mfr: 'ViaBlue',
    model: 'NF-S1 Silver Quattro Interconnect',
    cableType: 'interconnect',
    price: usd(150, 'CURRENT_RETAIL', 2024, 'Price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Germany',
    conductor: 'silver-plated-copper',
    geometry: 'star-quad',
    gauge: 24,
    dielectric: 'Polyethylene',
    shielding: 'Foil plus braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#0d6ea8',
    tags: ['silver-plated', 'shielded'],
    provenanceNote:
      'The Quattro name refers to the four-conductor star-quad core, which the maker documents. Gauge is the class norm for this cable and is estimated; capacitance is derived from the geometry rather than published.',
    notes:
      'Four silver-plated conductors in a star-quad core inside a foil-and-braid screen, with the maker\'s own T6s plugs. Star-quad puts each polarity on two diagonally opposite conductors, so the loop area presented to an external magnetic field very nearly cancels. Derived capacitance is 95 pF/m; on a 1 m pair that is 95 pF of load, which is inaudible against any normal line output and computed rather than assumed.',
  }),

  makeCable({
    mfr: 'ViaBlue',
    model: 'NF-S6 Silver Interconnect',
    cableType: 'interconnect',
    price: usd(280, 'CURRENT_RETAIL', 2024, 'Price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Germany',
    conductor: 'silver-plated-copper',
    geometry: 'braided',
    gauge: 23,
    dielectric: 'Polyethylene',
    shielding: 'Foil plus braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#0d6ea8',
    tags: ['silver-plated', 'shielded', 'upper-line'],
    provenanceNote:
      'Six-conductor construction from the maker\'s documentation; gauge estimated. Capacitance derived from the geometry, as no figure is published.',
    notes:
      'The senior analogue interconnect: six silver-plated conductors interleaved inside a double screen, terminated with T6s connectors. Derived capacitance 105 pF/m. At line level the only figure of the three that can matter is that one, and 105 pF against a 100 ohm source output puts the corner above 15 MHz.',
  }),

  makeCable({
    mfr: 'ViaBlue',
    model: 'X-20 Silver Power Cable',
    cableType: 'power',
    price: usd(130, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Germany',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 14,
    currentRatingA: 16,
    shielding: 'Foil screen',
    termination: ['IEC C13', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#0d6ea8',
    tags: ['silver-plated', 'shielded', 'entry-line'],
    provenanceNote:
      'The X-Series model number encodes conductor cross-section in tenths of a square millimetre, so X-20 is the 2.0 mm2 cord; the gauge here is that figure converted to AWG. Current rating is the European 16 A standard for this cross-section.',
    notes:
      'Three 2.0 mm2 silver-plated conductors, about 14 AWG, twisted and foil screened, rated 16 A. The naming convention is refreshingly literal: the number is the cross-section. Mains cords receive no signal processing in this engine, so what this entry contributes to a chain is an accurate description and an accurate price.',
  }),

  makeCable({
    mfr: 'ViaBlue',
    model: 'X-25 Silver Power Cable',
    cableType: 'power',
    price: usd(180, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Germany',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 16,
    shielding: 'Foil screen',
    termination: ['IEC C13', 'Schuko', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#0d6ea8',
    tags: ['silver-plated', 'shielded'],
    provenanceNote:
      'X-25 is the 2.5 mm2 cord in the series; gauge is that cross-section converted to AWG. Rating is the standard 16 A for the cross-section.',
    notes:
      'The 2.5 mm2 cord, roughly 13 AWG, screened and rated 16 A. The step from X-20 to X-25 is a 25 percent increase in copper and therefore a 25 percent reduction in the voltage the cord drops at a given current. That is a mains-side fact with no route to the audio, and the engine applies identity here.',
  }),

  makeCable({
    mfr: 'ViaBlue',
    model: 'X-40 Silver Power Cable',
    cableType: 'power',
    price: usd(300, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Germany',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 16,
    shielding: 'Foil plus braid',
    termination: ['IEC C13', 'IEC C19', 'Schuko', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#0d6ea8',
    tags: ['silver-plated', 'shielded', 'high-current'],
    provenanceNote:
      'X-40 is the 4.0 mm2 cord; gauge follows from that cross-section. Screen construction and connector options are from the maker\'s catalogue.',
    notes:
      'Three 4.0 mm2 conductors, about 11 AWG, double screened, with a C19 option. This is a genuinely heavy cord, sized the way a fixed installation would be sized. It is also, from the point of view of the audio signal, identical in effect to the cord that came free in the box, and this engine says so rather than implying otherwise.',
  }),

  makeCable({
    mfr: 'ViaBlue',
    model: 'X-60 Silver Power Cable',
    cableType: 'power',
    price: usd(450, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Germany',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 20,
    shielding: 'Foil plus braid',
    termination: ['IEC C19', 'Schuko', 'NEMA 5-20'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#0d6ea8',
    tags: ['silver-plated', 'shielded', 'flagship', 'high-current'],
    provenanceNote:
      'X-60 is the 6.0 mm2 cord in the series; the gauge given is the nearest AWG below that cross-section. Connector options from the maker\'s catalogue.',
    notes:
      'The largest cord in the X-Series, three conductors in the 6 mm2 class -- heavier than 10 AWG -- double screened and fitted with C19 and 20 A connectors. For a large power amplifier on a dedicated spur this is a sensible piece of installation hardware. No signal processing is applied to power cords in this engine, at any price.',
  }),

  makeCable({
    mfr: 'ViaBlue',
    model: 'KU-2 Silver USB Cable',
    cableType: 'usb',
    price: usd(90, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    country: 'Germany',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid',
    termination: ['USB A to B'],
    confidence: 'ESTIMATED',
    accent: '#0d6ea8',
    tags: ['usb-2.0', 'shielded', 'silver-plated'],
    provenanceNote:
      'Conductor material and screen from the maker\'s catalogue; gauge is the USB 2.0 class norm and is estimated, and the 90 ohm figure is the standard\'s requirement rather than a measurement of this assembly.',
    notes:
      'A screened USB 2.0 assembly with silver-plated conductors and moulded strain relief. Built to the standard, which is the only thing a USB cable can usefully be built to. The engine applies no signal processing to USB: the packets are checked on arrival and an asynchronous DAC clocks the conversion itself.',
  }),

  makeCable({
    mfr: 'ViaBlue',
    model: 'EP-7 Silver Network Cable',
    cableType: 'ethernet',
    price: usd(80, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m patch lead.'),
    priceLengthM: 1.5,
    country: 'Germany',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 100,
    digitalStandard: 'Cat 7, S/FTP',
    shielding: 'S/FTP, individually foiled pairs in an overall braid',
    termination: ['RJ45'],
    confidence: 'ESTIMATED',
    accent: '#0d6ea8',
    tags: ['cat7', 'shielded', 'silver-plated'],
    provenanceNote:
      'Category and shielding scheme from the maker\'s catalogue; gauge and the 100 ohm figure are requirements of the category rather than measurements of this lead.',
    notes:
      'A Cat 7 S/FTP patch lead: four individually foiled pairs inside an overall braid, terminated in shielded RJ45 plugs. It will pass the category test, which is the whole of what an Ethernet cable is asked to do. Ethernet is packetised, checksummed and retransmitted on error, and the engine applies no signal processing to it.',
  }),
];
